import { PolicyError, type JsonValue } from './types';

/** Version 1: sorted own object keys, JSON number/string encoding, ordered arrays. */
export function canonicalJson(value: unknown): string {
  const visiting = new Set<object>();
  function encode(input: unknown, depth: number): string {
    if (depth > 64) throw new PolicyError([{ code: 'INVALID_JSON', message: 'Payload exceeds the supported nesting depth.' }]);
    if (input === null) return 'null';
    if (typeof input === 'string' || typeof input === 'boolean') return JSON.stringify(input);
    if (typeof input === 'number' && Number.isFinite(input)) return JSON.stringify(input);
    if (typeof input !== 'object') throw new PolicyError([{ code: 'INVALID_JSON', message: 'Payload must contain only finite JSON values.' }]);
    if (visiting.has(input)) throw new PolicyError([{ code: 'INVALID_JSON', message: 'Payload contains a cycle.' }]);
    if (Object.getOwnPropertySymbols(input).length > 0) throw new PolicyError([{ code: 'INVALID_JSON', message: 'Symbol keys are not JSON payload fields.' }]);
    const prototype = Object.getPrototypeOf(input);
    if (!Array.isArray(input) && prototype !== Object.prototype && prototype !== null) {
      throw new PolicyError([{ code: 'INVALID_JSON', message: 'Payload objects must be plain JSON objects.' }]);
    }
    visiting.add(input);
    let result: string;
    if (Array.isArray(input)) {
      const values: string[] = [];
      for (let i = 0; i < input.length; i += 1) {
        const descriptor = Object.getOwnPropertyDescriptor(input, i);
        if (!descriptor) throw new PolicyError([{ code: 'INVALID_JSON', message: 'Sparse arrays are not valid payloads.' }]);
        if (!('value' in descriptor)) throw new PolicyError([{ code: 'INVALID_JSON', message: 'Payload getters are not permitted.' }]);
        values.push(encode(descriptor.value, depth + 1));
      }
      result = '[' + values.join(',') + ']';
    } else {
      const record = input as Record<string, unknown>;
      result = '{' + Object.keys(record).sort().map((key) => {
        const descriptor = Object.getOwnPropertyDescriptor(record, key);
        if (!descriptor || !('value' in descriptor)) throw new PolicyError([{ code: 'INVALID_JSON', message: 'Payload getters are not permitted.' }]);
        return JSON.stringify(key) + ':' + encode(descriptor.value, depth + 1);
      }).join(',') + '}';
    }
    visiting.delete(input);
    return result;
  }
  return encode(value, 0);
}

export async function hashPayload(value: JsonValue | unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalJson(value));
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return 'sha256:v1:' + Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Accept only explicit offsets; timezone-less local dates must be resolved by the caller. */
export function instant(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const parts = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d{1,3})?(Z|[+-]\d{2}:\d{2})$/.exec(value);
  if (!parts) return null;
  const [, yearText, monthText, dayText, hourText, minuteText, secondText] = parts;
  const year = Number(yearText), month = Number(monthText), day = Number(dayText);
  if (month < 1 || month > 12 || day < 1 || day > new Date(Date.UTC(year, month, 0)).getUTCDate()
      || Number(hourText) > 23 || Number(minuteText) > 59 || Number(secondText) > 59) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
